
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { FileText, Upload, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/components/auth/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

interface ProjectDocumentsProps {
  projectId: string;
  initialDocuments?: { name: string; url: string }[];
  canEdit?: boolean;
  isPublicView?: boolean;
}

export default function ProjectDocuments({ 
  projectId, 
  initialDocuments = [],
  canEdit = false,
  isPublicView = false
}: ProjectDocumentsProps) {
  const [documents, setDocuments] = useState<{ name: string; url: string }[]>(initialDocuments);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: profile } = useQuery({
    queryKey: ["profile-subscription", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("subscription_tier")
        .eq("id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching subscription tier:", error);
        return null;
      }
      
      return data;
    },
    enabled: !!user,
  });

  const isFreeTier = profile?.subscription_tier === 'free';

  const handleUpgradeClick = () => {
    navigate("/account");
    toast.info("Upgrade to Pro or Producer to upload documents");
  };

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEdit) {
      toast.error("You don't have permission to edit this project");
      return;
    }

    if (isFreeTier) {
      toast.error("Document uploads are available on Pro and Producer plans");
      handleUpgradeClick();
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const filePath = `${projectId}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("project-documents")
        .upload(filePath, file, { metadata: { size: file.size } });

      if (uploadError) {
        toast.error("Failed to upload document");
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from("project-documents")
        .getPublicUrl(filePath);

      const newDocuments = [...documents, { name: file.name, url: publicUrl }];
      const { error: updateError } = await supabase
        .from("projects")
        .update({ documents: newDocuments })
        .eq("id", projectId);

      if (updateError) {
        toast.error("Failed to update project documents");
        return;
      }

      setDocuments(newDocuments);
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      toast.success("Document uploaded successfully");
    } catch (error) {
      toast.error("An error occurred while uploading");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveDocument = async (documentUrl: string) => {
    if (!canEdit) {
      toast.error("You don't have permission to edit this project");
      return;
    }

    const newDocuments = documents.filter(d => d.url !== documentUrl);
    const { error } = await supabase
      .from("projects")
      .update({ documents: newDocuments })
      .eq("id", projectId);

    if (error) {
      toast.error("Failed to remove document");
      return;
    }

    setDocuments(newDocuments);
    queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    toast.success("Document removed successfully");
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-lg text-white">Project Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc, index) => (
              <div key={index} className="relative group bg-black/20 border border-white/10 rounded-lg p-4 hover:bg-black/30 transition-colors">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-white/70" />
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white hover:underline truncate"
                  >
                    {doc.name}
                  </a>
                </div>
                {canEdit && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemoveDocument(doc.url)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {canEdit && (
              isFreeTier ? (
                <div className="flex items-center justify-center border-2 border-dashed rounded-lg border-white/20 p-8">
                  <div className="text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="bg-white/5 p-3 rounded-full">
                        <Lock className="h-6 w-6 text-amber-500" />
                      </div>
                      <span className="text-sm text-white/70">
                        Premium Feature
                      </span>
                      <p className="text-xs text-white/50 max-w-xs">
                        Document uploads are available on Pro and Producer plans
                      </p>
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="mt-2" 
                        onClick={handleUpgradeClick}
                      >
                        Upgrade Now
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center border-2 border-dashed rounded-lg border-white/20 hover:border-white/40 transition-colors p-8">
                  <div className="text-center">
                    <input
                      type="file"
                      id="document-upload"
                      className="hidden"
                      onChange={handleDocumentUpload}
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="document-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="h-8 w-8 text-white/70" />
                      <span className="text-sm text-white/70">
                        {isUploading ? "Uploading..." : "Upload Document"}
                      </span>
                    </label>
                  </div>
                </div>
              )
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
